import { FancyImage } from "@/common-components/fancy-image/FancyImage";
import { getInspectProps } from "@/utils/getInspectProps";
import { cn } from "@/utils/tailwind-helper";
import { $, component$, useSignal } from "@builder.io/qwik";
import { useLocation } from "@builder.io/qwik-city";
import { Fulltext } from "~/common-components/fulltext/Fulltext";
import { PolymorphicElement } from "~/common-components/polymorphic-element/PolymorphicElement";
import { useCMSData, useUniversalText } from "~/hooks/useCMSData";
import { useCurrentLocale } from "~/hooks/useCurrentLocale";
import { BREAKPOINTS } from "../../constants/mediaquery";
import type { IGenBlogSection, IGenBlogSection_Text_Connections } from "../../services/graphql/__generated/sdk";
import { Button } from "../button/Button";
import { Newsletter } from "../newsletter/Newsletter";
import { SingleVideo } from "../single-video/SingleVideo";
import { BlogFullWidthImage } from "./BlogFullWidthImage";
import { BlogTableOfContent } from "./BlogTableOfContent";
import { BlogThreeImageGrid } from "./BlogThreeImageGrid";
import { BlogTwoImageGrid } from "./BlogTwoImageGrid";
import RichTextRenderer from "@caisy/rich-text-qwik-renderer";

interface DocumentLinkProps {
    connections: Array<IGenBlogSection_Text_Connections | null>;
    node: any;
    componentIndex: number;
}

const DocumentLink = component$<DocumentLinkProps>(({ connections, node, componentIndex }) => {
    return connections?.map((component) => {
        if (!component || node?.attrs?.documentId !== component.id) return null;
        return (
            <div class={`not-prose document-link ${component.__typename}`} key={component.id}>
                {component?.__typename == "Newsletter" && (
                    <Newsletter {...component} componentIndex={componentIndex} inline />
                )}

                {component?.__typename == "SingleVideo" && (
                    <SingleVideo {...component} componentIndex={componentIndex} inline />
                )}

                {component?.__typename == "Button" && <Button {...component} />}

                {component?.__typename == "BlogFullWidthImage" && <BlogFullWidthImage {...component} />}

                {component?.__typename == "BlogTwoImageGrid" && <BlogTwoImageGrid {...component} />}

                {component?.__typename == "BlogThreeImageGrid" && <BlogThreeImageGrid {...component} />}
            </div>
        );
    });
});

export interface BlogSectionProps extends IGenBlogSection {
    componentIndex: number;
}

export const BlogSection = component$<BlogSectionProps>(
    ({ id, title, publishDate, text, heroImage, author, componentIndex, teaserText }) => {
        const currentLocale = useCurrentLocale();
        const cmsData = useCMSData();
        const category = cmsData?.Page?.parentPage?.name;
        const firstAuthor = author?.[0];

        // useStylesScoped$(styles);
        const bodyRef = useSignal<HTMLDivElement>(null!);

        const dateObj = publishDate ? new Date(publishDate) : null;
        const formattedDate = dateObj?.toLocaleDateString(currentLocale === "en" ? "en-US" : "de-DE", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });

        const location = useLocation();
        const endsWithSlash = `${location?.url?.pathname}`.slice(-1) === "/";
        const currentPath = location?.url?.pathname || "/";

        const universalText = useUniversalText();

        return (
            <div
                class={cn(
                    "relative w-full bg-white",
                    "grid grid-cols-4 gap-x-4 gap-y-8 px-4",
                    "silver:grid-cols-8 silver:gap-x-6 silver:px-8",
                    "gold:grid-cols-12 gold:px-10",
                    "platinum:mx-auto platinum:max-w-[1440px]",
                )}
            >
                <div class={cn("col-span-full -mb-2 h-fit gold:col-span-2 gold:mb-0")}>
                    <a
                        class={cn("flex items-center gap-2 text-lg font-normal text-primary-500")}
                        href={currentPath
                            .split("/")
                            .slice(0, endsWithSlash ? -2 : -1)
                            .join("/")}
                        aria-label="Back"
                        target="_self"
                        rel="noopener_noreferrer"
                        onClick$={(e) => {
                            if (location.url.searchParams.get("listing") === "1") {
                                e.preventDefault();
                                history.back();
                            }
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M10 12L6 8L10 4"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                        {universalText.labelBack}
                    </a>
                </div>
                <div
                    class={cn(
                        "col-span-full flex flex-col gap-8 gold:col-span-8 gold:col-start-3 platinum:col-span-8 platinum:col-start-3",
                    )}
                >
                    <div class={cn("mt-1 flex flex-col gap-2 silver:gap-4")}>
                        {category && (
                            <a
                                href={currentPath
                                    .split("/")
                                    .slice(0, endsWithSlash ? -2 : -1)
                                    .join("/")}
                                aria-label="Back"
                                target="_self"
                                rel="noopener_noreferrer"
                            >
                                <p
                                    class={cn(
                                        "w-fit rounded bg-primary-50 px-2 py-1 text-xs font-medium text-primary-500/60",
                                    )}
                                    dangerouslySetInnerHTML={category.split("\n").join("<br />")}
                                />
                            </a>
                        )}
                        {title && (
                            <h2
                                {...getInspectProps({ id, fieldName: "title" })}
                                class={cn(
                                    "text-3xl font-normal text-primary-500 silver:text-4xl gold:text-5xl gold:leading-[100%]",
                                )}
                                dangerouslySetInnerHTML={title.split("\n").join("<br />")}
                            />
                        )}
                    </div>
                    <div class={cn("mt-1 flex flex-col gap-2 silver:gap-4")}>
                        {teaserText?.json && (
                            <span
                                {...getInspectProps({ id, fieldName: "teaserText" })}
                                class={cn(
                                    "text-base leading-6",
                                    "silver:eading-7 silver:text-xl",
                                    "silver:eading-7 silver:text-xl",
                                    "[&_a]:underline",
                                    "text-primary-500/80",
                                )}
                            >
                                <RichTextRenderer node={teaserText.json} />
                            </span>
                        )}
                    </div>
                    <div
                        class={cn(
                            "flex w-full flex-col gap-4 border-t border-primary-500/5 pt-4 silver:flex-row silver:justify-between",
                        )}
                    >
                        {firstAuthor && (
                            <div
                                class={cn("flex items-center gap-3")}
                                {...getInspectProps({ id, fieldName: "author" })}
                            >
                                {firstAuthor.avatar?.src && (
                                    <FancyImage
                                        img={firstAuthor.avatar}
                                        class={cn("h-11 w-11 rounded-full")}
                                        responsiveAspectRatio={{ bronze: 1 }}
                                        responsiveMaxWidth={{ bronze: 200 }}
                                    />
                                )}
                                {firstAuthor.name && (
                                    <div class={cn("flex flex-col")}>
                                        <p class={cn("text-base font-normal text-primary-500")}>{firstAuthor.name}</p>
                                        {firstAuthor.role && (
                                            <p class={cn("text-sm font-normal text-primary-500/80")}>
                                                {firstAuthor.role}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {formattedDate && (
                            <span
                                {...getInspectProps({ id, fieldName: "publishedDate" })}
                                class={cn("text-base font-normal text-primary-500/80")}
                            >
                                {formattedDate}
                            </span>
                        )}
                    </div>
                </div>
                {heroImage?.src && (
                    <div
                        class={cn("col-span-full py-4 gold:pb-4 gold:pt-8 platinum:py-8")}
                        {...getInspectProps({ id, fieldName: "heroImage" })}
                    >
                        <FancyImage
                            img={heroImage}
                            responsiveAspectRatio={{
                                bronze: 343 / 400,
                                silver: 770 / 550,
                                gold: 1200 / 600,
                                platinum: 1360 / 765,
                            }}
                            responsiveFactor={{
                                bronze: 1,
                                silver: 1,
                                gold: 1,
                                platinum: 1,
                            }}
                            responsiveMaxWidth={{
                                platinum: BREAKPOINTS.PLATINUM,
                            }}
                            lazyload={componentIndex > 0}
                            class="rounded-xl"
                        />
                    </div>
                )}
                {text?.json && (
                    <div
                        class={cn("gird relative col-span-full grid grid-cols-subgrid grid-rows-1")}
                        {...getInspectProps({ id, fieldName: "text" })}
                    >
                        <BlogTableOfContent text={text} bodyRef={bodyRef} />

                        <article
                            class={cn("blog-wrapper col-span-full row-start-1 grid grid-cols-subgrid")}
                            ref={bodyRef}
                        >
                            <Fulltext
                                text={text}
                                componentIndex={componentIndex}
                                class={cn(
                                    "blog-body col-span-full grid grid-cols-subgrid text-primary-500/80",
                                    // First rule for document links that aren't buttons
                                    "[&>*]:col-span-full",
                                    "gold:[&>.document-link:not(.Button)]:col-span-10",
                                    "gold:[&>.document-link:not(.Button)]:col-start-2",
                                    // Silver variant
                                    "silver:[&>*:not(.document-link)]:col-span-6",
                                    "silver:[&>*:not(.document-link)]:col-start-2",
                                    "silver:[&>.document-link.Button]:col-span-6",
                                    "silver:[&>.document-link.Button]:col-start-2",
                                    // Gold variant
                                    "gold:[&>*:not(.document-link)]:col-span-6",
                                    "gold:[&>*:not(.document-link)]:col-start-4",
                                    "gold:[&>.document-link.Button]:col-span-6",
                                    "gold:[&>.document-link.Button]:col-start-4",
                                    // margin top for linked components
                                    "[&>.document-link]:mt-12",
                                    "platinum:[&>.document-link]:mt-20",
                                )}
                                overwrites={{
                                    documentLink: $(
                                        (props) =>
                                            props?.node &&
                                            text?.connections && (
                                                <DocumentLink
                                                    node={props.node}
                                                    connections={text.connections}
                                                    componentIndex={componentIndex}
                                                />
                                            ),
                                    ),
                                    heading: $((props) => {
                                        if (!props?.node) return null;
                                        const level = props.node?.attrs?.level ?? 3;
                                        const textAlign = props.node?.attrs?.textAlign ?? "left";
                                        const id = props.node?.attrs?.id ?? "";
                                        return (
                                            <PolymorphicElement
                                                as={`h${level}`}
                                                style={{ textAlign: textAlign }}
                                                id={id}
                                                class={cn(
                                                    "[&>strong]:[font-size:inherit]",
                                                    "[&>strong]:[font-weight:inherit]",
                                                    (level === 1 || level === 2) && "article-section",
                                                )}
                                            >
                                                {props.children}
                                            </PolymorphicElement>
                                        );
                                    }),
                                }}
                            />
                        </article>
                    </div>
                )}
            </div>
        );
    },
);
